import type { Customer, CustomerLtlDefaults, LtlDeliveryServices } from '../../types/customer';
import type { DimensionUnit } from '../../types/dimensions';
import type { FreightNotificationType } from '../../types/freight-responses';
import type { LtlAddress, LtlBookingConfirmation, LtlHandlingUnit, LtlQuote, LtlShipment } from '../../types/ltl';
import type { State } from '../../types/reference-data';
import type { LtlPickupServices } from '../../types/ship-from';
import type { WeightUnit } from '../../types/weight';
import type { ShipFromOption } from '../freight-browser/freight-browser-types';
import type { FreightDensityAction } from '../freight-density/RecommendedFreightClassModal';

/**
 * The four wizard steps. There is intentionally NO 'quotes' step — in the new
 * design rates are always visible on the right-hand panel.
 */
export type WizardStepId = 'ship-from' | 'ship-to' | 'handling-units' | 'additional-details';

/**
 * How the wizard is opened:
 * - `new-from-quote`: fresh shipment from quote browser, mostly empty form but populate what we can from the quote browser.
 * - `new-direct`: new freight shipment from a direct launch of the booking wizard, no previous data to populate
 * - `load-saved-quote`: hydrated from a previously saved quote.
 * - `view-booked`: read-only confirmation view of an already-booked shipment.
 */
export const WIZARD_MODE = {
  NEW: 'new-from-quote',
  NEW_DIRECT: 'new-direct',
  LOAD_SAVED_QUOTE: 'load-saved-quote',
  VIEW_BOOKED: 'view-booked',
} as const;
export type WizardMode = (typeof WIZARD_MODE)[keyof typeof WIZARD_MODE];

/**
 * Narrow, wizard-shaped pickup services covering the nine Figma checkboxes.
 * Alias of {@link LtlPickupServices} — the wizard and the Ship From "Freight
 * Defaults" settings section share the exact same type and can never drift.
 * The container maps these to the wide backend `FreightRequestAccessorials`
 * enum at payload time — that mapping is NOT this component's concern.
 */
export type WizardPickupServices = LtlPickupServices;

/**
 * Narrow, wizard-shaped delivery services covering the delivery-services checkboxes.
 * Alias of {@link LtlDeliveryServices} — the wizard and the Customer "LTL Defaults"
 * settings section share the exact same type and can never drift. The container maps
 * these to the wide backend `FreightRequestAccessorials` enum at payload time — that
 * mapping is NOT this component's concern.
 *
 * TODO(backend mapping): "Hold Shipment at Terminal" (`isHoldAtTerminal`) and
 * "Grocery Consolidation Delivery" (`isGroceryConsolidationDelivery`) have no
 * obvious `LtlAdditionalServices` field — confirm backend field names (plan open
 * question #5).
 */
export type WizardDeliveryServices = LtlDeliveryServices;

export type WizardTradeshow = {
  name: string;
  boothNumber: string;
};

/**
 * Wizard insurance shape. The freight-browser already defines an equivalent
 * `FreightBrowserInsurance`, but that type lives in browser-specific territory;
 * to keep the wizard self-contained (and avoid coupling the two surfaces) we
 * define a dedicated wizard insurance type here. The container maps it to the
 * backend `LtlInsurance` at payload time.
 */
export type WizardInsurance = {
  isInsured: boolean;
  conditionOfGoods: string;
  category: string;
  insuredValue: number;
  // Structurally compatible with the freight-browser's `FreightBrowserInsurance`
  // so the wizard can pass `insurance` straight to the shared `InsuranceSection`.
  marksAndNumbers: string;
};

export type WizardHandlingCharge = {
  isEnabled: boolean;
  value: number;
};

export type WizardReferenceItem = {
  id: string;
  type: string;
  value: string;
};

export type WizardReferences = {
  isEnabled: boolean;
  items: WizardReferenceItem[];
};

export type WizardNotificationConfig = {
  /** React key only — never sent to the backend. */
  id: string;
  emails: string[];
  notificationTypes: FreightNotificationType[];
};

export type WizardNotifications = {
  isEnabled: boolean;
  recipientConfigs: WizardNotificationConfig[];
};

export type WizardShipmentServices = {
  isSortAndSegregate: boolean;
  isProtectFromFreeze: boolean;
};

/**
 * The single form-state model for the booking wizard, organized by step. The
 * container owns hydration/persistence; the wizard reads/writes via
 * `onFormChange`.
 */
export type FreightBookingFormState = {
  // Ship From
  selectedShipFromId: string;
  originAddress: LtlAddress;
  shipmentDate: string;
  pickupServices: WizardPickupServices;
  tradeshowPickup: WizardTradeshow;

  // Ship To
  destinationAddress: LtlAddress;
  shouldSaveAsNewCustomer: boolean;
  deliveryServices: WizardDeliveryServices;
  tradeshow: WizardTradeshow;
  /** Id of the customer whose LTL Defaults are currently loaded into `deliveryServices`/`deliveryInstructions`, or '' when none. */
  selectedCustomerId: string;
  /** When true, the container will persist delivery defaults to the selected customer on booking or save-draft. */
  shouldSaveLtlDeliveryDefaults: boolean;

  // Handling Units
  handlingUnits: LtlHandlingUnit[];

  // Additional Details
  shipmentServices: WizardShipmentServices;
  pickupReadyTime: string;
  pickupCloseTime: string;
  willArrangePickupSeparately: boolean;
  insurance: WizardInsurance;
  handlingCharge: WizardHandlingCharge;
  references: WizardReferences;
  notifications: WizardNotifications;
  pickupInstructions: string;
  deliveryInstructions: string;
  handlingInstructions: string;
  sellerFreightProviderID?: number;
  /** When true, the container will persist pickup defaults to the selected Ship From on booking or save-draft. */
  shouldSaveLtlDefaults: boolean;
};

export type WizardStepProps = {
  stepNumber: number;
  title: string;
  isActive: boolean;
  isCompleted: boolean;
  isWarning?: boolean;
  onEdit: () => void;
  onCollapse?: () => void;
  summary?: import('react').ReactNode;
  children: import('react').ReactNode;
};

export type FreightBookingWizardProps = {
  isOpen: boolean;
  mode: WizardMode;
  onClose: () => void;

  formState: FreightBookingFormState;
  onFormChange: (next: FreightBookingFormState) => void;

  shipFromOptions: ShipFromOption[];
  states: State[];
  // The weight unit commodity totals are displayed in (the seller's preferred
  // major unit — 'Pounds' or 'Kilograms'). Commodity weights may be stored in a
  // different unit and are converted to this for the handling-units summary.
  displayWeightUnit: WeightUnit;
  // The seller's preferred dimension unit — used to initialize new handling units
  // and as the fallback when a stored HU has no dimensionUnit set.
  displayDimensionUnit: DimensionUnit;
  currencyCode?: string;
  onCustomerSelect?: (customer: Customer) => void;
  onPasteAddressUsed?: () => void;
  onAddressValidated?: (address: LtlAddress) => void;
  onAddShipFrom?: () => void;
  onDensityAction?: (action: FreightDensityAction) => void;

  /** Opens the Ship From edit modal for the currently selected location. When absent the link is not rendered. */
  onEditShipFrom?: () => void;
  isSavingLtlDefaults?: boolean;
  canSaveLtlDefaults?: boolean;
  hasSavedLtlDefaults?: boolean;
  /** True while the customer's LTL Defaults are being fetched after a Ship To customer selection. */
  isLoadingCustomerLtlDefaults?: boolean;
  isSavingLtlDeliveryDefaults?: boolean;
  canSaveLtlDeliveryDefaults?: boolean;
  hasSavedLtlDeliveryDefaults?: boolean;
  /** The currently selected customer's saved LTL Defaults (or null), used by the Ship To step to detect unsaved changes. */
  loadedCustomerLtlDefaults?: CustomerLtlDefaults | null;
  onCommodityPresetSelect?: (presetId: number) => void;

  rates: LtlQuote[];
  isLoadingRates: boolean;
  rateError?: string;
  rateErrorCode?: string | null;
  selectedRate: LtlQuote | null;
  onSelectRate: (quote: LtlQuote) => void;
  onRefresh: () => void;

  onSaveDraft: () => void;
  onBook: () => void;
  isSavingDraft: boolean;
  isBooking: boolean;
  onDeleteDraft?: () => void;
  isDeletingDraft?: boolean;
  canDeleteDraft?: boolean;
  canManageBookings?: boolean;
  manageBookingsDisabledTooltip?: string;

  // Pre-completion (used by load-saved-quote hydration): which steps render as
  // already completed/collapsed and which step is initially active. Both are read
  // once on mount (lazy initial state); they do not drive the wizard afterward.
  initialCompletedSteps?: Partial<Record<WizardStepId, boolean>>;
  initialActiveSteps?: readonly WizardStepId[];

  bookingConfirmation?: LtlBookingConfirmation;
  bookedShipment?: LtlShipment;
  isHazmatEnabled?: boolean;
  isShipToAddressValidationEnabled?: boolean;
  zIndex?: number;
  isFreightNotificationsEnabled?: boolean;
};
